import React, { useState, useEffect } from 'react';
import { Mail, Phone, Trash2, Check, Eye } from 'lucide-react';
import { getContacts } from '../../services/api';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Messages = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchContacts = async () => {
    try {
      const data = await getContacts();
      setContacts(data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleMarkRead = async (contactId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contacts/${contactId}/read`);
      setContacts(prev => prev.map(c => 
        c.id === contactId ? { ...c, is_read: true } : c
      ));
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/contacts/${contactId}`);
      setContacts(prev => prev.filter(c => c.id !== contactId));
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const viewMessage = (message) => {
    setSelectedMessage(message);
    setShowDetails(true);
    if (!message.is_read) {
      handleMarkRead(message.id);
    }
  };

  const filteredContacts = contacts.filter(c => {
    if (filter === 'unread') return !c.is_read;
    if (filter === 'read') return c.is_read;
    return true;
  });

  const unreadCount = contacts.filter(c => !c.is_read).length;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 skeleton rounded-lg" />
        {[1,2,3,4].map(i => <div key={i} className="h-24 skeleton rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-1 mb-1">Messages</h1>
        <p className="body-small text-[var(--text-secondary)]">
          {contacts.length} total messages {unreadCount > 0 && `(${unreadCount} unread)`}
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'unread', 'read'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-full text-sm capitalize transition-all ${
              filter === status
                ? 'bg-[var(--text-primary)] text-white'
                : 'bg-white text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Messages List */}
      {filteredContacts.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <p className="text-[var(--text-secondary)]">No messages found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredContacts.map((contact) => (
            <div 
              key={contact.id} 
              className={`bg-white p-4 rounded-xl cursor-pointer hover:shadow-sm transition-shadow ${
                !contact.is_read ? 'border-l-4 border-[var(--accent-blush-dark)]' : ''
              }`}
              onClick={() => viewMessage(contact)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={`truncate ${!contact.is_read ? 'font-medium text-[var(--text-heading)]' : 'text-[var(--text-secondary)]'}`}>
                      {contact.name}
                    </h3>
                    {!contact.is_read && (
                      <span className="w-2 h-2 bg-[var(--accent-blush-dark)] rounded-full flex-shrink-0" />
                    )}
                  </div>
                  <p className="body-small text-[var(--text-secondary)] truncate">
                    {contact.subject || 'No subject'}
                  </p>
                  <p className="body-small text-[var(--text-light)] truncate mt-1">
                    {contact.message}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="body-small text-[var(--text-light)]">
                    {new Date(contact.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {!contact.is_read && (
                      <button
                        onClick={() => handleMarkRead(contact.id)}
                        className="p-1.5 rounded-lg hover:bg-[var(--bg-subtle)] transition-colors text-[var(--text-secondary)]"
                        title="Mark as read"
                      >
                        <Check size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(contact.id)}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-red-500"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Message Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-lg bg-white rounded-2xl">
          <DialogTitle className="heading-2 mb-4">Message Details</DialogTitle>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="p-4 bg-[var(--bg-subtle)] rounded-xl space-y-3">
                <div>
                  <p className="text-xs text-[var(--text-light)] uppercase tracking-wider mb-1">From</p>
                  <p className="font-medium text-[var(--text-heading)]">{selectedMessage.name}</p>
                </div>
                <div className="flex gap-4">
                  <a href={`mailto:${selectedMessage.email}`} className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-heading)]">
                    <Mail size={14} />
                    {selectedMessage.email}
                  </a>
                  {selectedMessage.phone && (
                    <a href={`tel:${selectedMessage.phone}`} className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-heading)]">
                      <Phone size={14} />
                      {selectedMessage.phone}
                    </a>
                  )}
                </div>
              </div>
              {selectedMessage.subject && (
                <div>
                  <p className="text-xs text-[var(--text-light)] uppercase tracking-wider mb-1">Subject</p>
                  <p className="text-[var(--text-heading)]">{selectedMessage.subject}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-[var(--text-light)] uppercase tracking-wider mb-1">Message</p>
                <p className="text-[var(--text-primary)] whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>
              <div className="pt-4 border-t border-[var(--border-light)] flex gap-3">
                <a
                  href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject || 'Your inquiry'}`}
                  className="btn-primary !py-2 !px-4 !min-w-0 flex-1 justify-center"
                >
                  <Mail size={16} />
                  Reply via Email
                </a>
                {selectedMessage.phone && (
                  <a
                    href={`tel:${selectedMessage.phone}`}
                    className="btn-secondary !py-2 !px-4 flex-1 justify-center"
                  >
                    <Phone size={16} />
                    Call
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
